import { InlineError, LoadingIndicator } from "@/components/QueryState";
import { api } from "@/lib/api";
import { policySummary, UPSTREAM_LABELS } from "@/lib/sso";
import { useQuery } from "@tanstack/react-query";

// Super-admin, read-only: each Boo's app sign-in providers and extra domains,
// and per-app policies.
export function OrgSignInPanel({ orgId }: { orgId: string }) {
  const query = useQuery({
    queryKey: ["admin-org-sso", orgId],
    queryFn: () => api.sso.adminOrg(orgId),
  });
  if (query.isLoading) return <LoadingIndicator />;
  if (query.isError) return <InlineError error={query.error} />;
  if (!query.data?.enabled || !query.data.data) {
    return <p className="text-sm text-muted-foreground">App sign-in is not configured on this backend.</p>;
  }
  const { boos, apps } = query.data.data;

  return (
    <div className="space-y-4">
      {boos.length === 0 ? (
        <p className="text-sm text-muted-foreground">No Boo has apps registered for sign-in.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase text-muted-foreground">
                <th className="px-3 py-2 text-left font-medium">Boo</th>
                <th className="px-3 py-2 text-left font-medium">Providers</th>
                <th className="px-3 py-2 text-left font-medium">Extra domains</th>
                <th className="px-3 py-2 text-left font-medium">Issuer</th>
              </tr>
            </thead>
            <tbody>
              {boos.map((b) => (
                <tr key={b.containerId} className="border-t align-top">
                  <td className="px-3 py-2 font-medium">{b.containerName ?? b.containerId}</td>
                  <td className="px-3 py-2">
                    {b.settings.enabledUpstreams.map((u) => UPSTREAM_LABELS[u]).join(", ") || "None"}
                    <span className="block text-xs text-muted-foreground">{b.settings.source}</span>
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{b.settings.extraDomains.join(", ") || "none"}</td>
                  <td className="px-3 py-2 font-mono text-xs break-all">{b.settings.issuer}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {apps.length === 0 ? (
        <p className="text-sm text-muted-foreground">No apps registered for sign-in.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase text-muted-foreground">
                <th className="px-3 py-2 text-left font-medium">App</th>
                <th className="px-3 py-2 text-left font-medium">Boo</th>
                <th className="px-3 py-2 text-left font-medium">Policy</th>
                <th className="px-3 py-2 text-left font-medium">Domains / emails</th>
              </tr>
            </thead>
            <tbody>
              {apps.map((a) => (
                <tr key={a.clientId} className="border-t align-top">
                  <td className="px-3 py-2 font-medium">{a.appId}</td>
                  <td className="px-3 py-2 text-muted-foreground">{a.containerName ?? a.containerId ?? "—"}</td>
                  <td className="px-3 py-2">
                    {a.policy.mode}
                    <span className="block text-xs text-muted-foreground">{policySummary(a.policy)}</span>
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground break-all">
                    {[...a.policy.domains, ...a.policy.emails].join(", ") || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
